import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, Shield, Sparkles, Building2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";

export const PricingSection = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: "Choisissez le plan qui vous convient",
      subtitle: "Pas de frais cachés. Annulez à tout moment.",
      perMonth: "/mois",
      recommended: "Recommandé",
      trialNote: "14 jours d'essai gratuit. Annulable à tout moment.",
      ctaStarter: "Démarrer avec Starter",
      ctaPro: "Démarrer avec Pro",
      ctaBusiness: "Démarrer avec Business",
      
      // Starter Plan
      starterName: "Starter",
      starterDesc: "Gestion centralisée Google Business",
      starterTagline: "Idéal pour les indépendants et auto-entrepreneurs",
      starterPrice: "49€",
      starterFeatures: [
        { text: "Gestion Google Business centralisée", included: true },
        { text: "50 avis / mois", included: true },
        { text: "Assistant IA", included: true },
        { text: "QR Code standard", included: true },
        { text: "Centre de Contrôle basique", included: true },
        { text: "Multi-plateforme (FB, Yelp...)", included: false },
        { text: "Kit Print Premium", included: false },
      ],

      // Pro Plan
      proName: "Pro",
      proDesc: "Accès Multi-plateforme complet",
      proTagline: "Le choix des commerçants et restaurateurs",
      proPrice: "99€",
      proFeatures: [
        { text: "Google + Facebook + Yelp + Trustpilot + TripAdvisor", included: true },
        { text: "Avis illimités", included: true },
        { text: "Assistant IA personnalisé", included: true },
        { text: "Brouillons IA avec validation manuelle", included: true },
        { text: "Kit QR Premium imprimable", included: true },
        { text: "Analytics avancés", included: true },
        { text: "Support prioritaire", included: true },
      ],

      // Business Plan
      businessName: "Business",
      businessDesc: "Multi-établissements & Réseau",
      businessTagline: "Pour les franchises et réseaux multi-établissements",
      businessPrice: "199€",
      businessFeatures: [
        { text: "Tout Pro inclus", included: true },
        { text: "Jusqu'à 5 établissements", included: true },
        { text: "Rapports PDF automatisés", included: true },
        { text: "Dashboard centralisé multi-sites", included: true },
        { text: "Alertes négatives en temps réel", included: true },
        { text: "Account manager dédié", included: true },
        { text: "Onboarding personnalisé", included: true },
      ],
    },
    en: {
      title: "Choose the plan that fits your business",
      subtitle: "No hidden fees. Cancel anytime.",
      perMonth: "/month",
      recommended: "Most Popular",
      trialNote: "14-day free trial. Cancel anytime.",
      ctaStarter: "Start with Starter",
      ctaPro: "Start with Pro",
      ctaBusiness: "Start with Business",
      
      // Starter Plan
      starterName: "Starter",
      starterDesc: "Centralized Google Business Management",
      starterTagline: "Perfect for freelancers and single-location businesses",
      starterPrice: "$49",
      starterFeatures: [
        { text: "Centralized Google Business management", included: true },
        { text: "50 reviews / month", included: true },
        { text: "AI Assistant", included: true },
        { text: "Standard QR Code", included: true },
        { text: "Basic Reputation Hub", included: true },
        { text: "Multi-platform (FB, Yelp...)", included: false },
        { text: "Premium Print Kit", included: false },
      ],

      // Pro Plan
      proName: "Pro",
      proDesc: "Full Multi-Platform Access",
      proTagline: "The choice of ambitious local businesses",
      proPrice: "$99",
      proFeatures: [
        { text: "Google + Facebook + Yelp + Trustpilot + TripAdvisor", included: true },
        { text: "Unlimited reviews", included: true },
        { text: "Custom AI Assistant", included: true },
        { text: "AI drafts with manual validation", included: true },
        { text: "Premium printable QR Kit", included: true },
        { text: "Advanced analytics", included: true },
        { text: "Priority support", included: true },
      ],

      // Business Plan
      businessName: "Business",
      businessDesc: "Multi-Location & Network",
      businessTagline: "Scale your reputation across all your locations",
      businessPrice: "$199",
      businessFeatures: [
        { text: "Everything in Pro", included: true },
        { text: "Up to 5 locations", included: true },
        { text: "Automated PDF reports", included: true },
        { text: "Multi-site centralized dashboard", included: true },
        { text: "Real-time negative alerts", included: true },
        { text: "Dedicated account manager", included: true },
        { text: "Personalized onboarding", included: true },
      ],
    },
  };

  const t = content[language];

  const plans = [
    {
      name: t.starterName,
      description: t.starterDesc,
      tagline: t.starterTagline,
      price: t.starterPrice,
      features: t.starterFeatures,
      icon: Zap,
      highlighted: false,
      cta: t.ctaStarter,
      link: "/signup?plan=starter",
      showTrialNote: true,
    },
    {
      name: t.proName,
      description: t.proDesc,
      tagline: t.proTagline,
      price: t.proPrice,
      features: t.proFeatures,
      icon: Sparkles,
      highlighted: true,
      cta: t.ctaPro,
      link: "/signup?plan=pro",
      badge: t.recommended,
      showTrialNote: true,
    },
    {
      name: t.businessName,
      description: t.businessDesc,
      tagline: t.businessTagline,
      price: t.businessPrice,
      features: t.businessFeatures,
      icon: Building2,
      highlighted: false,
      cta: t.ctaBusiness,
      link: "/signup?plan=business",
      showTrialNote: true,
    },
  ];

  return (
    <section className="py-28 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground">{t.subtitle}</p>
        </motion.div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative rounded-3xl p-8 ${
                plan.highlighted
                  ? "bg-white border-2 border-primary shadow-2xl shadow-primary/20 scale-105 z-10"
                  : "bg-white border border-slate-200 shadow-lg"
              }`}
            >
              {/* Recommended Badge */}
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="bg-primary text-primary-foreground text-sm font-semibold px-5 py-1.5 rounded-full shadow-lg">
                    {plan.badge}
                  </div>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 ${
                  plan.highlighted 
                    ? "bg-gradient-to-br from-primary to-primary/80 shadow-lg shadow-primary/25" 
                    : "bg-slate-100"
                }`}>
                  <plan.icon className={plan.highlighted ? "text-white" : "text-slate-600"} size={28} />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-1">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
                {plan.tagline && (
                  <p className="text-xs text-primary font-medium mt-2 italic">{plan.tagline}</p>
                )}
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold text-foreground">{plan.price}</span>
                  <span className="text-lg text-muted-foreground">{t.perMonth}</span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center gap-3">
                    {feature.included ? (
                      <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                        <Check className="text-accent" size={12} strokeWidth={3} />
                      </div>
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <X className="text-slate-400" size={12} strokeWidth={3} />
                      </div>
                    )}
                    <span className={`text-sm ${feature.included ? "text-foreground" : "text-muted-foreground"}`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Link to={plan.link} className="block">
                <Button
                  size="lg"
                  className={`w-full h-12 font-semibold ${
                    plan.highlighted
                      ? "btn-premium"
                      : "bg-slate-900 hover:bg-slate-800"
                  }`}
                >
                  {plan.cta}
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
              
              {/* Trial Note - shown below button for Pro and Business */}
              {plan.showTrialNote && (
                <p className="text-xs text-muted-foreground text-center mt-3">
                  {t.trialNote}
                </p>
              )}
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};
